import { Controller } from 'react-hook-form';
import {
  Select,
  InputText,
  InputNumber,
  TransactionTypeEnum,
  IconInfo,
  Checkbox,
  SelectType,
  SelectItemProps,
  IconLoader,
} from '@ui/src';
import {
  ClientType,
  CounterPartyEnum,
  ScoreActionEnum,
  TransactionMonitoringRuleEntity,
  PeriodUnitEnum,
} from '../../hoc/types';
import { TransactionMonitoringRuleFormTypes } from './types';

import cn from './form.module.css';

const TransactionMonitoringRuleForm = ({
  control,
  errors,
  ruleId,
  isEditable,
  entity,
  transactionType,
  paymentTypes,
  setValue,
  clearErrors,
  riskLevels,
  organizations,
  organizationsPagination,
  clientType,
  nextOrganizationsLoading,
  organizationsIsLoading,
  onInfinateScroll,
  onSearchOrganization,
  selectedOrganizations,
  setSelectedOrganizations,
  countriesList,
  currenciesList,
  action,
  counterParty,
  periodUnit,
  setPeriodUnit,
  periodOfTimeInHours,
  organizationsByIdsIsLoading,
}: TransactionMonitoringRuleFormTypes) => (
  <div className={cn.ruleForm}>
    <div className={cn.ruleFormBlockTitle}>General</div>
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="name"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <InputText
            name="ruleName"
            placeholder="Enter Rule name"
            onChange={onChange}
            value={value}
            title="Rule name"
            error={errors?.name?.message}
            disabled={Boolean(ruleId && !isEditable)}
            tabIndex={ruleId && !isEditable ? -1 : 0}
          />
        )}
      />
    </div>
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="entity"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <>
            <Select
              list={[
                {
                  name: 'Transaction',
                  value: TransactionMonitoringRuleEntity.TRANSACTION,
                },
                {
                  name: 'Payment Order',
                  value: TransactionMonitoringRuleEntity.PAYMENT_ORDER,
                },
              ]}
              placeholder="Choose Rule Entity"
              name="entity"
              title="Rule Entity"
              value={value}
              onChange={(e) => {
                onChange(e);

                if (
                  e.target.value ===
                  TransactionMonitoringRuleEntity.PAYMENT_ORDER
                ) {
                  setValue('action', ScoreActionEnum.AML_REVIEW);

                  if (errors?.action?.message) {
                    clearErrors(['action']);
                  }
                } else {
                  setValue('action', '');
                }
              }}
              selectWrapperClassName={`${cn.ruleSelect} ${
                errors?.entity?.message ? cn.ruleSelectError : ''
              }`}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
              withClearButton
              fullWidth
            />
            {errors?.entity?.message && (
              <div className={cn.error}>{errors?.entity?.message}</div>
            )}
          </>
        )}
      />
    </div>
    {(entity === TransactionMonitoringRuleEntity.TRANSACTION ||
      (ruleId && transactionType)) && (
      <div className={cn.ruleFormGroup}>
        <Controller
          control={control}
          name="transactionType"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <>
              <Select
                list={[
                  {
                    name: 'Internal',
                    value: TransactionTypeEnum.INTERNAL,
                  },
                  {
                    name: 'Incoming',
                    value: TransactionTypeEnum.INCOMING,
                  },
                  {
                    name: 'Outgoing',
                    value: TransactionTypeEnum.OUTGOING,
                  },
                ]}
                placeholder="All types"
                name="transactionType"
                title="Transaction type"
                value={value}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.transactionType?.message ? cn.ruleSelectError : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}
                error={errors?.transactionType?.message}
                withClearButton
                fullWidth
              />
              {!transactionType && (
                <div className={cn.counterPartyDescription}>
                  If not set, the rule applies to incoming, outgoing and
                  internal transactions
                </div>
              )}
            </>
          )}
        />
      </div>
    )}
    {(transactionType === TransactionTypeEnum.INCOMING ||
      transactionType === TransactionTypeEnum.OUTGOING) && (
      <div className={cn.ruleFormGroup}>
        <Controller
          control={control}
          name="paymentType"
          defaultValue={undefined}
          render={({ field: { onChange, value } }) => (
            <>
              <Select
                list={paymentTypes}
                placeholder="Choose Payment type"
                name="paymentType"
                title="Payment type"
                value={value as string}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.paymentType?.message ? cn.ruleSelectError : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}
                withClearButton
                fullWidth
              />
              {errors?.paymentType?.message && (
                <div className={cn.error}>{errors?.paymentType?.message}</div>
              )}
            </>
          )}
        />
      </div>
    )}
    {entity === TransactionMonitoringRuleEntity.PAYMENT_ORDER && (
      <div className={cn.ruleFormGroup}>
        <Controller
          control={control}
          name="paymentOrderType"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <>
              <Select
                list={[
                  {
                    name: 'Cash Collection Single',
                    value: 'CASH_COLLECTION_SINGLE_OUTPUT',
                  },
                ]}
                placeholder="Choose Payment Order type"
                name="paymentOrderType"
                title="Payment Order type"
                value={value}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.paymentOrderType?.message ? cn.ruleSelectError : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}
                fullWidth
              />
              {errors?.paymentOrderType?.message && (
                <div className={cn.error}>
                  {errors?.paymentOrderType?.message}
                </div>
              )}
            </>
          )}
        />
      </div>
    )}
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="clientType"
        defaultValue={undefined}
        render={({ field: { onChange, value } }) => (
          <>
            <Select
              list={[
                {
                  name: 'Individual',
                  value: ClientType.INDIVIDUAL,
                },
                {
                  name: 'Corporate',
                  value: ClientType.CORPORATE,
                },
              ]}
              placeholder="Choose Client type"
              name="clientType"
              title="Client type"
              value={value}
              onChange={(e) => {
                onChange(e);

                if (selectedOrganizations?.length) {
                  setSelectedOrganizations([]);
                }
              }}
              selectWrapperClassName={`${cn.ruleSelect} ${
                errors?.clientType?.message ? cn.ruleSelectError : ''
              }`}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
              hintText={
                entity === TransactionMonitoringRuleEntity.PAYMENT_ORDER
                  ? 'For Payment Orders, client type is resolved automatically using the organization ID from the payment order or the recipient account.'
                  : ''
              }
              withClearButton
              fullWidth
            />
            {errors?.clientType?.message && (
              <div className={cn.error}>{errors?.clientType?.message}</div>
            )}
          </>
        )}
      />
    </div>
    {entity === TransactionMonitoringRuleEntity.TRANSACTION && (
      <>
        <div className={cn.ruleFormGroup}>
          <Controller
            control={control}
            name="clientRiskScoreRangeIds"
            defaultValue={[]}
            render={({ field: { onChange, value, name } }) => (
              <Select
                list={riskLevels?.map((item) => ({
                  name: item?.name,
                  value: item?.id,
                }))}
                placeholder="Choose client risk category"
                name={name}
                title="Client risk category"
                value={value}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.clientRiskScoreRangeIds?.message
                    ? cn.ruleSelectError
                    : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}

                mode={SelectType.MULTIPLE}
                error={errors?.clientRiskScoreRangeIds?.message}
                fullWidth
              />
            )}
          />
        </div>
        {clientType && (
          <div className={cn.ruleFormGroup}>
            {!organizationsByIdsIsLoading && (
              <Controller
                control={control}
                name="organizationIds"
                defaultValue={[]}
                render={({ field: { onChange, value, name } }) => (
                  <Select
                    list={organizations?.map((item) => ({
                      name:
                        item?.type === 'INDIVIDUAL'
                          ? `${item?.firstName} ${item?.lastName}`
                          : item?.companyName || '',
                      value: item?.id,
                      description:
                        item?.type === 'INDIVIDUAL'
                          ? `phone: ${item?.user?.phone}`
                          : item?.companyNumber,
                    }))}
                    placeholder="All organisations"
                    name={name}
                    title="Organisations"
                    value={value}
                    onChange={(e) => {
                      onChange(e);

                      let nextSelectedOrganization = (
                        [] as SelectItemProps[]
                      ).concat(selectedOrganizations);

                      (e.target.value as string[])?.forEach((item) => {
                        const currentOrganization = organizations?.find(
                          (organization) => organization.id === item,
                        );

                        if (
                          currentOrganization &&
                          !nextSelectedOrganization?.find(
                            (organization) =>
                              organization?.value === currentOrganization?.id,
                          )
                        ) {
                          nextSelectedOrganization.push({
                            name:
                              currentOrganization?.type === 'INDIVIDUAL'
                                ? `${currentOrganization?.firstName} ${currentOrganization?.lastName}`
                                : currentOrganization?.companyName || '',
                            value: currentOrganization.id,
                          });
                        }
                      });

                      nextSelectedOrganization =
                        nextSelectedOrganization?.filter((item) =>
                          (e.target.value as string[]).includes(
                            item.value as string,
                          ),
                        );

                      setSelectedOrganizations(nextSelectedOrganization);
                    }}
                    selectedValuesList={selectedOrganizations}
                    selectWrapperClassName={`${cn.ruleSelect} ${
                      errors?.organizationIds?.message ? cn.ruleSelectError : ''
                    }`}
                    disabled={Boolean(ruleId && !isEditable)}
                    tabIndex={ruleId && !isEditable ? -1 : 0}
                    mode={SelectType.MULTIPLE}
                    error={errors?.organizationIds?.message}
                    nextPageIsLoading={nextOrganizationsLoading}
                    hasNextPage={organizationsPagination?.hasNextPage}
                    isLoading={organizationsIsLoading}
                    onInfinateScroll={onInfinateScroll}
                    onSearch={onSearchOrganization}
                    withInfiniteScroll
                    fullWidth
                  />
                )}
              />
            )}
            {organizationsByIdsIsLoading && (
              <div className={cn.organizationsLoader}>
                <div className={cn.organizationsLoaderTitle}>Organisations</div>
                <div className={cn.organizationsLoaderIcon}>
                  <IconLoader width={24} height={24} />
                </div>
              </div>
            )}
          </div>
        )}
      </>
    )}

    <div className={cn.ruleFormSeparator} />
    <div className={cn.ruleFormBlockTitle}>Counterparty</div>
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="counterParty"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <>
            <Select
              list={[
                {
                  name: 'Any',
                  value: CounterPartyEnum.ANY,
                },
                {
                  name: 'Same',
                  value: CounterPartyEnum.SAME,
                },
                {
                  name: 'New',
                  value: CounterPartyEnum.NEW,
                },
              ]}
              placeholder="Choose counterparty"
              name="counterParty"
              title="Counterparty"
              value={value}
              onChange={onChange}
              selectWrapperClassName={`${cn.ruleSelect} ${
                errors?.counterParty?.message ? cn.ruleSelectError : ''
              }`}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
              error={errors?.counterParty?.message}
              fullWidth
            />
            {counterParty === CounterPartyEnum.NEW && (
              <div className={cn.counterPartyDescription}>
                Applies to the first transaction with a counterparty new to the
                organisation
              </div>
            )}
          </>
        )}
      />
    </div>
    {entity === TransactionMonitoringRuleEntity.TRANSACTION && (
      <>
        <div className={cn.ruleFormGroup}>
          <Controller
            control={control}
            name="counterpartyCountryIds"
            defaultValue={[]}
            render={({ field: { onChange, value, name } }) => (
              <Select
                list={countriesList || []}
                placeholder="Choose countries"
                name={name}
                title="Counterparty countries"
                value={value}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.counterpartyCountryIds?.message
                    ? cn.ruleSelectError
                    : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}
                error={errors?.counterpartyCountryIds?.message}
                mode={SelectType.MULTIPLE}
                fullWidth
              />
            )}
          />
        </div>
        <div className={cn.ruleFormGroup}>
          <Controller
            control={control}
            name="counterpartyCountryRiskScoreRangeIds"
            defaultValue={[]}
            render={({ field: { onChange, value, name } }) => (
              <Select
                list={riskLevels?.map((item) => ({
                  name: item?.name,
                  value: item?.id,
                }))}
                placeholder="Choose risk levels"
                name={name}
                title="Counterparty country risk levels"
                value={value}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.counterpartyCountryRiskScoreRangeIds?.message
                    ? cn.ruleSelectError
                    : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}

                mode={SelectType.MULTIPLE}
                error={errors?.counterpartyCountryRiskScoreRangeIds?.message}
                fullWidth
              />
            )}
          />
        </div>
        <div className={cn.ruleFormGroup}>
          <Controller
            control={control}
            name="counterpartyBankCountryIds"
            defaultValue={[]}
            render={({ field: { onChange, value, name } }) => (
              <Select
                list={countriesList}
                placeholder="All countries"
                name={name}
                title="Counterparty bank countries"
                value={value}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.counterpartyBankCountryIds?.message
                    ? cn.ruleSelectError
                    : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}

                mode={SelectType.MULTIPLE}
                error={errors?.counterpartyBankCountryIds?.message}
                fullWidth
              />
            )}
          />
        </div>
        <div className={cn.ruleFormGroup}>
          <Controller
            control={control}
            name="counterpartyBankCountryRiskScoreRangeIds"
            defaultValue={[]}
            render={({ field: { onChange, value, name } }) => (
              <Select
                list={riskLevels?.map((item) => ({
                  name: item?.name,
                  value: item?.id,
                }))}
                placeholder="Choose risk levels"
                name={name}
                title="Counterparty bank country risk levels"
                value={value}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.counterpartyBankCountryRiskScoreRangeIds?.message
                    ? cn.ruleSelectError
                    : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}

                mode={SelectType.MULTIPLE}
                error={
                  errors?.counterpartyBankCountryRiskScoreRangeIds?.message
                }
                fullWidth
              />
            )}
          />
        </div>
      </>
    )}

    <div className={cn.ruleFormSeparator} />
    <div className={cn.ruleFormBlockTitle}>Conditions</div>
    {entity === TransactionMonitoringRuleEntity.PAYMENT_ORDER && (
      <div className={cn.paymentOrderHint}>
        <div className={cn.iconInfoWrapper}>
          <IconInfo width={16} height={16} />
        </div>
        <div className={cn.paymentOrderHintText}>
          Transactions will be considered related only if sender name, address,
          and date of birth are identical.
        </div>
      </div>
    )}
    {entity === TransactionMonitoringRuleEntity.TRANSACTION && (
      <>
        <div className={cn.ruleFormGroup}>
          <Controller
            control={control}
            name="currencyIds"
            defaultValue={[]}
            render={({ field: { onChange, value, name } }) => (
              <Select
                list={currenciesList || []}
                placeholder="Choose currencies"
                name={name}
                title="Currency"
                value={value}
                onChange={onChange}
                selectWrapperClassName={`${cn.ruleSelect} ${
                  errors?.currencyIds?.message ? cn.ruleSelectError : ''
                }`}
                disabled={Boolean(ruleId && !isEditable)}
                tabIndex={ruleId && !isEditable ? -1 : 0}
                mode={SelectType.MULTIPLE}
                error={errors?.currencyIds?.message}
                fullWidth
              />
            )}
          />
        </div>
        <div className={`${cn.ruleFormGroup} ${cn.ruleFormGroupFlex}`}>
          <div className={cn.ruleFormGroupFlexInput}>
            <Controller
              control={control}
              name="singleTransactionAmountFrom"
              render={({ field: { onChange, value, name } }) => (
                <InputNumber
                  name={name}
                  placeholder="Enter amount from"
                  onChange={(e) => {
                    onChange(e);

                    if (Number(e.target.value) > 999999999) {
                      onChange('999999999');
                    }
                  }}
                  value={value || ''}
                  title="Single transaction amount from"
                  error={errors?.singleTransactionAmountFrom?.message}
                  prefix="From"
                  prefixClassName={cn.volumePrefix}
                  labelClassName={`${cn.transactionVolumeInput} ${
                    ruleId && !isEditable ? cn.inputWithPrefixDisaled : ''
                  }`}
                  disabled={Boolean(ruleId && !isEditable)}
                  tabIndex={ruleId && !isEditable ? -1 : 0}
                />
              )}
            />
          </div>
          <div className={cn.ruleFormGroupFlexInput}>
            <Controller
              control={control}
              name="singleTransactionAmountTo"
              render={({ field: { onChange, value, name } }) => (
                <InputNumber
                  name={name}
                  placeholder="Enter amount to"
                  onChange={(e) => {
                    onChange(e);

                    if (Number(e.target.value) > 999999999) {
                      onChange('999999999');
                    }
                  }}
                  value={value || ''}
                  title="singleTransactionAmountTo"
                  error={errors?.singleTransactionAmountTo?.message}
                  prefix="To"
                  prefixClassName={cn.volumePrefix}
                  labelClassName={`${cn.transactionVolumeInput} ${
                    ruleId && !isEditable ? cn.inputWithPrefixDisaled : ''
                  }`}
                  disabled={Boolean(ruleId && !isEditable)}
                  tabIndex={ruleId && !isEditable ? -1 : 0}
                />
              )}
            />
          </div>
        </div>
      </>
    )}

    <div
      className={`${cn.ruleFormGroup} ${entity === TransactionMonitoringRuleEntity.TRANSACTION ? cn.ruleFormGroupFlex : ''}`}
    >
      {entity === TransactionMonitoringRuleEntity.TRANSACTION ? (
        <>
          <div className={cn.ruleFormGroupFlexInput}>
            <Controller
              control={control}
              name="number"
              render={({ field: { onChange, value, name } }) => (
                <InputNumber
                  name={name}
                  placeholder="Enter number from"
                  onChange={(e) => onChange(e)}
                  value={value || ''}
                  title="Number of transactions from"
                  error={errors?.number?.message}
                  labelClassName={`${cn.transactionNumbersInput} ${
                    ruleId && !isEditable ? cn.inputWithPrefixDisaled : ''
                  }`}
                  prefix={
                    <div className={cn.ruleTransactionNumberPrefix}>≥</div>
                  }
                  disabled={Boolean(ruleId && !isEditable)}
                  tabIndex={ruleId && !isEditable ? -1 : 0}
                />
              )}
            />
          </div>
          <div className={cn.ruleFormGroupFlexInput}>
            <Controller
              control={control}
              name="numberTo"
              render={({ field: { onChange, value, name } }) => (
                <InputNumber
                  name={name}
                  placeholder="Enter number to"
                  onChange={(e) => onChange(e)}
                  value={value || ''}
                  title="Number of transactions to"
                  error={errors?.numberTo?.message}
                  labelClassName={`${cn.transactionNumbersInput} ${
                    ruleId && !isEditable ? cn.inputWithPrefixDisaled : ''
                  }`}
                  prefix={
                    <div className={cn.ruleTransactionNumberPrefix}>≤</div>
                  }
                  disabled={Boolean(ruleId && !isEditable)}
                  tabIndex={ruleId && !isEditable ? -1 : 0}
                />
              )}
            />
          </div>
        </>
      ) : (
        <Controller
          control={control}
          name="number"
          render={({ field: { onChange, value, name } }) => (
            <InputNumber
              name={name}
              placeholder="Enter Number of transactions"
              onChange={(e) => onChange(e)}
              value={value || ''}
              title="Number of transactions"
              error={errors?.number?.message}
              labelClassName={`${cn.transactionNumbersInput} ${
                ruleId && !isEditable ? cn.inputWithPrefixDisaled : ''
              }`}
              prefix={<div className={cn.ruleTransactionNumberPrefix}>≥</div>}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
            />
          )}
        />
      )}
    </div>
    <div className={`${cn.ruleFormGroup} ${cn.ruleFormGroupFlex}`}>
      <div className={cn.ruleFormGroupFlexInput}>
        <Controller
          control={control}
          name="volumeFrom"
          render={({ field: { onChange, value } }) => (
            <InputNumber
              name="volumeFrom"
              placeholder="Enter vol. from"
              onChange={(e) => {
                onChange(e);

                if (Number(e.target.value) > 999999999) {
                  onChange('999999999');
                }
              }}
              value={value}
              title="Transactions vol. from"
              error={errors?.volumeFrom?.message}
              prefix="From"
              prefixClassName={cn.volumePrefix}
              labelClassName={`${cn.transactionVolumeInput} ${
                ruleId && !isEditable ? cn.inputWithPrefixDisaled : ''
              }`}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.ruleFormGroupFlexInput}>
        <Controller
          control={control}
          name="volumeTo"
          render={({ field: { onChange, value } }) => (
            <InputNumber
              name="volumeTo"
              placeholder="Enter vol. to"
              onChange={(e) => {
                onChange(e);

                if (Number(e.target.value) > 999999999) {
                  onChange('999999999');
                }
              }}
              value={value}
              title="Transactions vol. to"
              error={errors?.volumeTo?.message}
              prefix="To"
              prefixClassName={cn.volumePrefix}
              labelClassName={`${cn.transactionVolumeInput} ${
                ruleId && !isEditable ? cn.inputWithPrefixDisaled : ''
              }`}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
    </div>
    <div className={`${cn.ruleFormGroup} ${cn.ruleFormGroupFlex}`}>
      <div className={cn.ruleFormGroupFlexInput}>
        <Controller
          control={control}
          name="periodOfTimeInHours"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="periodOfTimeInHours"
              placeholder="Enter period"
              onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
              value={value}
              title="Period"
              error={errors?.periodOfTimeInHours?.message}
              disabled={Boolean(ruleId && !isEditable)}
              tabIndex={ruleId && !isEditable ? -1 : 0}
            />
          )}
        />
      </div>
      <div className={cn.ruleFormGroupFlexInput}>
        <Select
          list={[
            {
              name: 'Hours',
              value: PeriodUnitEnum.HOURS,
            },
            {
              name: 'Days',
              value: PeriodUnitEnum.DAYS,
            },
          ]}
          placeholder="Choose unit"
          name="periodUnit"
          title="Unit"
          value={periodUnit}
          onChange={(e) => {
            setPeriodUnit(e.target.value as PeriodUnitEnum);

            if (periodOfTimeInHours) {
              let nextPeriod;

              if (e.target.value === PeriodUnitEnum.DAYS) {
                nextPeriod = Number(periodOfTimeInHours) / 24;
              } else if (e.target.value === PeriodUnitEnum.HOURS) {
                nextPeriod = Number(periodOfTimeInHours) * 24;
              }

              setValue('periodOfTimeInHours', nextPeriod as number);
            }
          }}
          selectWrapperClassName={cn.ruleSelect}
          disabled={Boolean(ruleId && !isEditable)}
          tabIndex={ruleId && !isEditable ? -1 : 0}
          fullWidth
        />
      </div>
    </div>

    <div className={cn.ruleFormSeparator} />
    <div className={cn.ruleFormBlockTitle}>Actions</div>
    <div className={cn.ruleFormGroup}>
      <Controller
        control={control}
        name="action"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <>
            {entity !== TransactionMonitoringRuleEntity.PAYMENT_ORDER && (
              <div className={cn.ruleFormRadioWrapper}>
                <Checkbox
                  type="radio"
                  title="OPS Review"
                  name="action"
                  onChange={() => onChange(ScoreActionEnum.OPS_REVIEW)}
                  checked={Boolean(value === ScoreActionEnum.OPS_REVIEW)}
                  visualClassName={
                    errors?.action?.message ? cn.checkboxError : ''
                  }
                />
              </div>
            )}
            <div className={cn.ruleFormRadioWrapper}>
              <Checkbox
                type="radio"
                title="AML Review"
                name="action"
                onChange={() => onChange(ScoreActionEnum.AML_REVIEW)}
                checked={Boolean(value === ScoreActionEnum.AML_REVIEW)}
                visualClassName={
                  errors?.action?.message ? cn.checkboxError : ''
                }
              />
            </div>
            <div className={cn.ruleFormRadioWrapper}>
              <Checkbox
                type="radio"
                title="Pass"
                name="action"
                onChange={() => onChange(ScoreActionEnum.PASS)}
                checked={Boolean(value === ScoreActionEnum.PASS)}
                visualClassName={
                  errors?.action?.message ? cn.checkboxError : ''
                }
              />
            </div>
            {action === ScoreActionEnum.PASS && (
              <div className={cn.counterPartyDescription}>
                Pass: the warning is closed automatically and the transaction is
                not held. Use for post-factum review.
              </div>
            )}

            {errors?.action?.message && (
              <div className={cn.error}>{errors?.action?.message}</div>
            )}
          </>
        )}
      />
    </div>
  </div>
);

export default TransactionMonitoringRuleForm;
